'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

type SidebarTheme = 'dark' | 'light';

interface SidebarContextValue {
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
  toggleExpanded: () => void;
  theme: SidebarTheme;
  setTheme: (theme: SidebarTheme) => void;
  toggleTheme: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

const STORAGE_KEYS = {
  EXPANDED: 'sidebar-expanded',
  THEME: 'sidebar-theme',
} as const;

interface SidebarProviderProps {
  children: React.ReactNode;
  defaultExpanded?: boolean;
  defaultTheme?: SidebarTheme;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
  defaultExpanded = true,
  defaultTheme = 'dark',
}) => {
  const [isExpanded, setIsExpandedState] = useState(defaultExpanded);
  const [theme, setThemeState] = useState<SidebarTheme>(defaultTheme);
  const [isHydrated, setIsHydrated] = useState(false);

  const applyTheme = useCallback((nextTheme: SidebarTheme, animateGuard = true) => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    if (animateGuard) {
      root.classList.add('theme-switching');
      window.setTimeout(() => {
        root.classList.remove('theme-switching');
      }, 120);
    }

    localStorage.setItem(STORAGE_KEYS.THEME, nextTheme);
    root.setAttribute('data-theme', nextTheme);
    root.style.colorScheme = nextTheme;
  }, []);

  // Hydrate from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedExpanded = localStorage.getItem(STORAGE_KEYS.EXPANDED);
      const storedTheme = localStorage.getItem(STORAGE_KEYS.THEME) as SidebarTheme | null;

      if (storedExpanded !== null) {
        setIsExpandedState(storedExpanded === 'true');
      }

      let finalTheme: SidebarTheme;
      if (storedTheme && (storedTheme === 'dark' || storedTheme === 'light')) {
        finalTheme = storedTheme;
      } else {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        finalTheme = prefersDark ? 'dark' : 'light';
      }

      setThemeState(finalTheme);
      applyTheme(finalTheme, false);

      setIsHydrated(true);
    }
  }, [applyTheme]);

  // Handle responsive behavior
  useEffect(() => {
    if (!isHydrated || typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 1024 && width >= 768) {
        // Tablet: collapsed by default
        setIsExpandedState(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isHydrated]);

  const setIsExpanded = useCallback((value: boolean) => {
    setIsExpandedState(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.EXPANDED, String(value));
    }
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpandedState((prev) => {
      const newValue = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEYS.EXPANDED, String(newValue));
      }
      return newValue;
    });
  }, []);

  const setTheme = useCallback(
    (newTheme: SidebarTheme) => {
      setThemeState(newTheme);
      applyTheme(newTheme);
    },
    [applyTheme]
  );

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      applyTheme(newTheme);
      return newTheme;
    });
  }, [applyTheme]);

  return (
    <SidebarContext.Provider
      value={{
        isExpanded,
        setIsExpanded,
        toggleExpanded,
        theme,
        setTheme,
        toggleTheme,
      }}>
      {children}
    </SidebarContext.Provider>
  );
};

// Default values for SSR or when used outside provider
const defaultContextValue: SidebarContextValue = {
  isExpanded: true,
  setIsExpanded: () => {},
  toggleExpanded: () => {},
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
};

export const useSidebar = (): SidebarContextValue => {
  const context = useContext(SidebarContext);
  // Return default values instead of throwing to prevent SSR crashes
  if (!context) {
    return defaultContextValue;
  }
  return context;
};

/**
 * Pins the classic theme for a subtree without touching the document (no data-theme, no storage). The Atlas workspace
 * renders classic chat components outside the classic layout, where the context default ('dark') would otherwise
 * apply; it passes its own light/dark theme here (WP-LEAD-DS).
 */
export const SidebarThemeOverride: React.FC<{ theme: SidebarTheme; children: React.ReactNode }> = ({
  theme,
  children,
}) => {
  const outer = useSidebar();
  return <SidebarContext.Provider value={{ ...outer, theme }}>{children}</SidebarContext.Provider>;
};

export default SidebarContext;
